import React from "react";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import { FileExportId, GenericExperienceFe } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useFileExportExperience } from "@src/api/analytics/file-export-experience";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { useDispatch, useSelector } from "@src/store";
import { isDialogOpen, setDialog } from "@src/store/slices/global";
import { theme } from "@src/theme";
import { dataExport } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface DataExportDialogProps {
  experience: GenericExperienceFe;
  showDataReconciliationExport?: boolean;
}

export const DataExportDialog = ({
  showDataReconciliationExport,
  experience,
}: DataExportDialogProps) => {
  const isOpen = useSelector(isDialogOpen("dataExport"));
  const dispatch = useDispatch();
  const user = useLoggedInUser();
  const fileExport = useFileExportExperience({
    experience,
    graphConfigs: [],
    overviewDataLoaded: true,
  });
  const isAdmin = useIsAdmin();

  const isLoading = fileExport.isPending;
  const experienceId = useSelector(
    (state) => state.dashboardFilters.experienceId
  );

  const handleClose = () => {
    dispatch(setDialog({ dialog: "dataExport", open: false }));
  };

  const handleExperimentDownload = (value: FileExportId) => {
    if (experienceId && user.isSuccess) {
      fileExport.mutate(value);

      handleClose();
    }
  };

  return (
    <IgDialog
      title={"Export Your Data"}
      handleClose={handleClose}
      handleYesClick={handleClose}
      showCloseIcon={true}
      showDialog={isOpen}
      dialogHeight={null}
      hideActions={true}
      rawContent={
        <Grid
          container
          direction={"column"}
          justifyContent={"center"}
          rowSpacing={2}
        >
          <Grid item>
            <IgTypography>
              Choose an option below and we will provide a downloadable link at
              the top of the screen:
            </IgTypography>
          </Grid>
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"center"}
            columnSpacing={2}
          >
            <Grid item>
              <IgButton
                loading={isLoading}
                variant={"outlined"}
                sx={{
                  border: `1px solid ${theme.igBorders.core.default}`,
                  backgroundColor: "white",
                }}
                onClick={() => {
                  handleExperimentDownload("lineItemsExport");
                }}
              >
                Line Items
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton
                sx={{
                  border: `1px solid ${theme.igBorders.core.default}`,
                  backgroundColor: "white",
                }}
                loading={isLoading}
                variant={"outlined"}
                onClick={() => {
                  handleExperimentDownload("ordersExport");
                }}
              >
                Orders
              </IgButton>
            </Grid>
            {showDataReconciliationExport || isAdmin ? (
              <Grid item>
                <IgButton
                  sx={{
                    border: `1px solid ${theme.igBorders.core.default}`,
                    backgroundColor: "white",
                  }}
                  loading={isLoading}
                  variant={"outlined"}
                  onClick={() => {
                    handleExperimentDownload("ordersReconciliationExport");
                  }}
                >
                  Order Reconciliation
                </IgButton>
              </Grid>
            ) : null}
          </Grid>
          <Grid item>
            <IgTypography>
              Learn more about exporting your data{" "}
              <IgTypography
                igStyle={"link"}
                onClick={() => openNewTab(dataExport, "Experiment Data Export")}
              >
                here.
              </IgTypography>
            </IgTypography>
          </Grid>
        </Grid>
      }
      loading={isLoading}
    />
  );
};
